// Fila de envio de aulas pelo navegador.
//
// - Um vídeo por vez; dentro dele, 3 partes de 16 MB em paralelo.
// - Cada parte é repetida até 5 vezes se a rede falhar (com espera crescente).
// - A fila vive fora do React: dá para navegar pelo console enquanto envia.
// - Um envio interrompido (página recarregada) pode ser retomado escolhendo o
//   mesmo arquivo de novo: só as partes que faltam são enviadas.

import { ApiError, type Upload } from "@acervo/api-client";

export type Destination = { courseId: string } | { newCourse: { title: string; owned: boolean } };

export interface CreateRequest {
  filename: string;
  size: number;
  content_type: string;
  course_id?: string;
  new_course?: { title?: string; owned?: boolean; ref?: string };
  module_title: string;
  lesson_title: string;
}

export interface Deps {
  create(req: CreateRequest): Promise<Upload>;
  putPart(id: string, n: number, body: Blob, signal: AbortSignal): Promise<void>;
  complete(id: string): Promise<Upload>;
  cancel(id: string): Promise<void>;
  sleep?: (ms: number) => Promise<void>;
}

export type JobState = "waiting" | "sending" | "finishing" | "sent" | "error" | "canceled";

export interface Job {
  key: string;
  file: File;
  lessonTitle: string;
  moduleTitle: string;
  courseLabel: string;
  state: JobState;
  sentBytes: number;
  upload?: Upload;
  error?: string;
}

interface Internal extends Job {
  dest: Destination | { ref: string } | { resume: Upload };
  abort: AbortController;
}

const PARALLEL = 3;
const ATTEMPTS = 5;

let seq = 0;

export class UploadQueue {
  private jobs: Internal[] = [];
  private listeners = new Set<() => void>();
  private running = false;
  private snapshot: Job[] = [];
  private readonly deps: Deps;

  constructor(deps: Deps) {
    this.deps = deps;
  }

  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => void this.listeners.delete(l);
  };

  getSnapshot = (): Job[] => this.snapshot;

  /** Há envio em andamento (para avisar antes de fechar a página). */
  get busy(): boolean {
    return this.jobs.some((j) => j.state === "waiting" || j.state === "sending" || j.state === "finishing");
  }

  /** Põe vídeos na fila, na ordem recebida, todos no mesmo destino e módulo. */
  add(items: { file: File; lessonTitle: string }[], dest: Destination, moduleTitle: string, courseLabel: string) {
    for (const it of items) {
      this.jobs.push({
        key: `j${++seq}`,
        file: it.file,
        lessonTitle: it.lessonTitle,
        moduleTitle,
        courseLabel,
        state: "waiting",
        sentBytes: 0,
        dest,
        abort: new AbortController(),
      });
    }
    this.emit();
    void this.run();
  }

  /** Retoma um envio interrompido com o mesmo arquivo. */
  resume(upload: Upload, file: File): string | null {
    if (file.name !== upload.filename || file.size !== upload.size) {
      return `Escolha o mesmo arquivo: ${upload.filename} (${upload.size.toLocaleString("pt-BR")} bytes).`;
    }
    this.jobs.push({
      key: `j${++seq}`,
      file,
      lessonTitle: upload.lesson_title,
      moduleTitle: upload.module_title,
      courseLabel: upload.course_title,
      state: "waiting",
      sentBytes: 0,
      upload,
      dest: { resume: upload },
      abort: new AbortController(),
    });
    this.emit();
    void this.run();
    return null;
  }

  /** Cancela um envio da fila (e no servidor, se já tiver começado). */
  async cancel(key: string) {
    const j = this.jobs.find((x) => x.key === key);
    if (!j || j.state === "sent" || j.state === "canceled") return;
    j.abort.abort();
    j.state = "canceled";
    this.emit();
    if (j.upload) await this.deps.cancel(j.upload.id).catch(() => {});
  }

  /** Tira da lista os envios que já terminaram. */
  clearFinished() {
    this.jobs = this.jobs.filter((j) => j.state === "waiting" || j.state === "sending" || j.state === "finishing");
    this.emit();
  }

  private emit() {
    this.snapshot = this.jobs.map(({ dest: _d, abort: _a, ...pub }) => ({ ...pub }));
    this.listeners.forEach((l) => l());
  }

  private async run() {
    if (this.running) return;
    this.running = true;
    try {
      for (let j = this.next(); j; j = this.next()) {
        await this.send(j);
      }
    } finally {
      this.running = false;
    }
  }

  private next(): Internal | undefined {
    return this.jobs.find((j) => j.state === "waiting");
  }

  /** O "ref" do curso novo criado pelo primeiro vídeo do mesmo lote. */
  private refs = new WeakMap<Destination, string>();

  private async send(j: Internal) {
    j.state = "sending";
    this.emit();
    try {
      let up: Upload;
      if ("resume" in j.dest) {
        up = j.dest.resume;
      } else {
        const req: CreateRequest = {
          filename: j.file.name,
          size: j.file.size,
          content_type: j.file.type || "application/octet-stream",
          module_title: j.moduleTitle,
          lesson_title: j.lessonTitle,
        };
        const d = j.dest;
        if ("courseId" in d) req.course_id = d.courseId;
        else if ("newCourse" in d) {
          const ref = this.refs.get(d);
          req.new_course = ref ? { ref } : { title: d.newCourse.title, owned: d.newCourse.owned };
        }
        up = await this.deps.create(req);
        if ("newCourse" in d && up.new_course_ref) this.refs.set(d, up.new_course_ref);
      }
      j.upload = up;

      const done = new Set(up.parts_done);
      const partBytes = (n: number) => Math.min(up.part_size, up.size - (n - 1) * up.part_size);
      j.sentBytes = [...done].reduce((s, n) => s + partBytes(n), 0);
      this.emit();

      const pending: number[] = [];
      for (let n = 1; n <= up.parts_total; n++) if (!done.has(n)) pending.push(n);

      const worker = async () => {
        for (let n = pending.shift(); n !== undefined; n = pending.shift()) {
          const start = (n - 1) * up.part_size;
          const blob = j.file.slice(start, start + partBytes(n));
          await this.withRetry(() => this.deps.putPart(up.id, n!, blob, j.abort.signal), j.abort.signal);
          j.sentBytes += blob.size;
          this.emit();
        }
      };
      await Promise.all(Array.from({ length: Math.min(PARALLEL, pending.length) }, worker));

      j.state = "finishing";
      this.emit();
      j.upload = await this.deps.complete(up.id);
      j.state = "sent";
    } catch (err) {
      if (j.abort.signal.aborted) j.state = "canceled";
      else {
        j.state = "error";
        j.error = err instanceof Error ? err.message : String(err);
      }
    }
    this.emit();
  }

  private async withRetry(fn: () => Promise<void>, signal: AbortSignal) {
    const sleep = this.deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
    for (let attempt = 1; ; attempt++) {
      try {
        return await fn();
      } catch (err) {
        const permanent =
          err instanceof ApiError && err.status >= 400 && err.status < 500 && err.status !== 408 && err.status !== 429;
        if (signal.aborted || permanent || attempt >= ATTEMPTS) throw err;
        await sleep(1000 * 2 ** (attempt - 1));
      }
    }
  }
}

/** "03 - Fase_interna (final).mp4" → "Fase interna (final)". */
export function titleFromFilename(name: string): string {
  const base = name.replace(/\.[^.]+$/, "");
  const t = base
    .replace(/^\s*(aula|parte|ep|episódio)?\s*\d+\s*[-–_.)]*\s*/i, "")
    .replace(/[_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const out = t || base.trim();
  return out.charAt(0).toUpperCase() + out.slice(1);
}

/** Ordena como o Explorador do Windows: "2 - x" antes de "10 - x". */
export function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, "pt-BR", { numeric: true, sensitivity: "base" });
}
